import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Target, CalendarClock, ArrowRight, ListChecks, CheckCircle2, ClipboardList } from "lucide-react";
import dayjs from "dayjs";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import { APPRAISAL_CYCLES, APPRAISALS } from "../../constants/api";

import EmployeePublishedSummary from "../../components/appraisal/EmployeePublishedSummary";
import RMTeamSummary from "../../components/appraisal/RMTeamSummary";
import HRGovernanceSummary from "../../components/appraisal/HRGovernanceSummary";
import useAppraisalRole from "../../hooks/useAppraisalRole";

// Server-side status strings (must match emedhrmsapi/appraisal/constants.py).
const STATUS = {
    PUBLISHED: "published",
    CLOSED: "closed",
    PENDING_EMPLOYEE_SUBMISSION: "pending_employee_submission",
    IN_PROGRESS: "in_progress",
    SENT_BACK_BY_RM: "sent_back_by_rm",
};

// Render every section a user qualifies for, so a user who is both HR and RM
// and also has their own pending appraisal sees all three blocks stacked,
// each backed by its own scoped API call (mine / team / all).
const AppraisalDashboard = () => {
    const navigate = useNavigate();
    const { fetch } = useApiMethods();
    const { user, isHR, isRM } = useAppraisalRole();

    const myEmployeeId = user?.employee_id ? String(user.employee_id) : null;

    const [loading, setLoading] = useState(true);
    const [activeCycle, setActiveCycle] = useState(null);
    const [mineList, setMineList] = useState([]);
    const [teamList, setTeamList] = useState([]);
    const [allList, setAllList] = useState([]);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const calls = [
                fetch(APPRAISAL_CYCLES, { status: "active" }),
                fetch(APPRAISALS, { scope: "mine" }),
            ];
            if (isRM) calls.push(fetch(APPRAISALS, { scope: "team" }));
            if (isHR) calls.push(fetch(APPRAISALS, { scope: "all" }));

            const results = await Promise.all(calls);
            let i = 0;
            const cycles = results[i++]?.data || [];
            const mine = results[i++]?.data || [];
            const team = isRM ? (results[i++]?.data || []) : [];
            const all = isHR ? (results[i++]?.data || []) : [];

            setActiveCycle(cycles[0] || null);
            setMineList(mine);
            setTeamList(team);
            setAllList(all);
        } catch (_) {
            setActiveCycle(null);
            setMineList([]);
            setTeamList([]);
            setAllList([]);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isHR, isRM]);

    useEffect(() => { loadData(); }, [loadData]);

    // Personal published appraisal — only one owned by the logged-in user, not
    // someone else whose record happens to come back in team/all scope.
    const myPublished = useMemo(() => {
        const owned = mineList.filter((a) => {
            // Defensive: if employee.id is somehow not present, fall back to
            // treating the whole `mine` list as owned (the backend already
            // filters by employee for scope=mine).
            const empId = a.employee?.id;
            return empId == null || (myEmployeeId && String(empId) === myEmployeeId);
        });
        return owned
            .filter((a) => a.status === STATUS.PUBLISHED || a.status === STATUS.CLOSED)
            .sort((a, b) => new Date(b.published_at || b.modified_date) - new Date(a.published_at || a.modified_date))[0]
            || null;
    }, [mineList, myEmployeeId]);

    // Personal pending — the user's own appraisal that still needs their
    // submission. Shown in a small "next action" card on every role.
    const myPending = useMemo(() => {
        return mineList.find((a) => [STATUS.PENDING_EMPLOYEE_SUBMISSION, STATUS.IN_PROGRESS, STATUS.SENT_BACK_BY_RM].includes(a.status))
            || null;
    }, [mineList]);

    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={Target}
                title="Appraisal Dashboard"
                subtitle={user?.name ? `Welcome, ${user.name}${user.designation ? ` (${user.designation})` : ""}` : "Performance management overview"}
            />

            <div className="px-3 pb-6 space-y-4">
                {/* Active cycle banner — universal context for every role. */}
                {activeCycle && (
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gradient-to-r from-sidebar/10 to-transparent dark:from-sidebar-primary/15 dark:to-transparent p-4 sm:p-5">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                                    <CalendarClock className="w-3.5 h-3.5" />
                                    <span>Active Cycle</span>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{activeCycle.name}</h3>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    {dayjs(activeCycle.period_start).format("DD MMM YYYY")} – {dayjs(activeCycle.period_end).format("DD MMM YYYY")}
                                    {" · "}
                                    Self-eval window {dayjs(activeCycle.self_eval_start).format("DD MMM")}–{dayjs(activeCycle.self_eval_end).format("DD MMM")}
                                </p>
                            </div>
                            {isHR && (
                                <Button
                                    onClick={() => navigate(`/appraisal/cycles/${activeCycle.id}`)}
                                    className="bg-sidebar hover:bg-sidebar-primary text-white whitespace-nowrap"
                                    size="sm"
                                >
                                    View Cycle <ArrowRight className="w-4 h-4 ml-1" />
                                </Button>
                            )}
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="text-center py-10 text-sm text-muted-foreground">Loading dashboard…</div>
                ) : (
                    <>
                        {/* ── Personal section — only the logged-in user's own records ── */}
                        {myPublished ? (
                            <EmployeePublishedSummary appraisal={myPublished} />
                        ) : myPending ? (
                            <div className="rounded-lg border border-blue-200 dark:border-blue-900/40 bg-blue-50/60 dark:bg-blue-900/15 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 text-xs text-blue-700 dark:text-blue-300 mb-1">
                                        <ClipboardList className="w-3.5 h-3.5" />
                                        <span>Action Required — Your Appraisal</span>
                                    </div>
                                    <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                                        Complete your self-evaluation for {myPending.cycle_name || "the current cycle"}
                                    </h3>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        Current status: {myPending.status.replaceAll("_", " ")}
                                    </p>
                                </div>
                                <Button
                                    size="sm"
                                    className="bg-sidebar hover:bg-sidebar-primary text-white"
                                    onClick={() => navigate(`/appraisal/my-appraisal/${myPending.id}`)}
                                >
                                    Open My Appraisal <ArrowRight className="w-4 h-4 ml-1" />
                                </Button>
                            </div>
                        ) : (
                            mineList.length === 0 && (
                                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 sm:p-5 text-center text-sm text-muted-foreground">
                                    You don't have an appraisal yet for the current cycle.
                                </div>
                            )
                        )}

                        {/* ── RM section — team aggregations. Only when the user manages a team. ── */}
                        {isRM && teamList.length > 0 && (
                            <RMTeamSummary appraisals={teamList} />
                        )}

                        {/* ── HR section — org-wide aggregations. Only for HR / admin. ── */}
                        {isHR && (
                            <HRGovernanceSummary appraisals={allList} />
                        )}

                        {/* Quick actions strip — visible to every role, role-aware buttons. */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
                            <h3 className="text-base font-semibold mb-3 text-gray-900 dark:text-gray-100">Quick Actions</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => navigate("/appraisal/my-appraisal")}
                                    className="justify-start"
                                >
                                    <ClipboardList className="w-4 h-4 mr-2" /> My Appraisal
                                </Button>
                                {isRM && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => navigate("/appraisal/team-review")}
                                        className="justify-start"
                                    >
                                        <ClipboardList className="w-4 h-4 mr-2" /> Team Review
                                    </Button>
                                )}
                                {(isRM || isHR) && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => navigate("/appraisal/kpi-manager")}
                                        className="justify-start"
                                    >
                                        <ListChecks className="w-4 h-4 mr-2" /> Manage KPIs
                                    </Button>
                                )}
                                {isHR && (
                                    <>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => navigate("/appraisal/cycles/new")}
                                            className="justify-start"
                                        >
                                            <CalendarClock className="w-4 h-4 mr-2" /> Create Cycle
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => navigate("/appraisal/final-approval")}
                                            className="justify-start"
                                        >
                                            <CheckCircle2 className="w-4 h-4 mr-2" /> Final Approval
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </ContainerCard>
    );
};

export default AppraisalDashboard;
